/**
 * Reads the per-meeting initial dialog config from sessionStorage.
 *
 * NewMeetingDialog persists the chosen language(s), auto-detect flag, and
 * batch/live mode under `meeting-config:<meetingId>` so that on a hard
 * refresh of the live recording route the wrapper can hydrate with the
 * same defaults the user picked. Missing or malformed payloads yield a
 * `null` result and the wrapper falls back to live mode + the company
 * `default_language` (see `resolveRecordingStart`).
 */
import { useMemo } from "react"
import type { TRecordingMode } from "../interfaces"

interface IInitialDialogConfig {
  langs: string[] | null
  autoDetect: boolean
  mode: TRecordingMode | null
}

export function useDialogConfig(meetingId: string): IInitialDialogConfig | null {
  return useMemo(() => {
    if (typeof window === "undefined") return null
    const raw = sessionStorage.getItem(`meeting-config:${meetingId}`)
    if (!raw) return null
    try {
      const parsed = JSON.parse(raw) as {
        languages?: unknown
        autoDetect?: unknown
        mode?: unknown
      }
      const langs = Array.isArray(parsed.languages)
        ? parsed.languages.filter((l): l is string => typeof l === "string")
        : null
      const autoDetect = typeof parsed.autoDetect === "boolean" ? parsed.autoDetect : false
      const mode: TRecordingMode | null =
        parsed.mode === "batch" ? "batch" : parsed.mode === "live" || parsed.mode === "streaming" ? "streaming" : null
      return { langs, autoDetect, mode }
    } catch {
      return null
    }
  }, [meetingId])
}
